import React, { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Table,
  FormFeedback,
  Modal,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import Header from 'components/Headers/Header';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { customDropdownWithBorder } from 'util/reactCustomStyles';
import useCustomPmrPrograms from 'util/useCustomPmrPrograms';
import Swal from 'sweetalert2';
import classnames from 'classnames';
import { getPlanById, getPlansForSpecialty } from 'config/subscriptionPlans';
import { SPECIALTIES } from 'config/specialtyConfig';
import {
  createGroupOrder,
  createGroupOrderReset,
  fetchGroupOrders,
  fetchGroupOrderDetails,
  approveGroupOrderRequest,
  approveGroupOrderRequestReset,
  rejectGroupOrderRequest,
  rejectGroupOrderRequestReset,
} from 'redux/GroupOrders/GroupOrdersActions';
import GroupOrderDetailsModal from './GroupOrderDetailsModal';

const ManageGroupOrdersPage = (props) => {
  const dispatch = useDispatch();
  const allPmrProgramOptions = useCustomPmrPrograms();
  const [activeTab, setActiveTab] = useState('1');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);
  const ordersPerPage = 10;

  // Form state
  const [programName, setProgramName] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [items, setItems] = useState([{ planId: null, plan: null, quantity: '' }]);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [paymentType, setPaymentType] = useState('coordinator');
  const [errors, setErrors] = useState({});

  // Approval modal state
  const [approvalModalOpen, setApprovalModalOpen] = useState(false);
  const [approvalOrder, setApprovalOrder] = useState(null);
  const [approvalPaymentType, setApprovalPaymentType] = useState('coordinator');
  const [approvalDiscount, setApprovalDiscount] = useState(20);
  const [approvalYear, setApprovalYear] = useState(null);
  const [approvalItems, setApprovalItems] = useState([]);

  const yearOptions = [
    { value: 'Fellow', label: 'Fellow' },
    { value: 'PGY 1', label: 'PGY 1' },
    { value: 'PGY 2', label: 'PGY 2' },
    { value: 'PGY 3', label: 'PGY 3' },
    { value: 'PGY 4', label: 'PGY 4' },
  ];

  const roleOptions = [
    { value: 'Current Resident', label: 'Current Resident' },
    { value: 'Program Director', label: 'Program Director' },
    { value: 'Program Coordinator', label: 'Program Coordinator' },
  ];

  const specialtyPlans = getPlansForSpecialty(props.activeSpecialty);
  const planOptions = specialtyPlans.map((p) => ({
    value: p.id,
    label: `${p.name} — $${p.price}`,
    plan: p,
  }));

  // Reset plan line items and pagination when specialty changes via the sidebar switcher
  useEffect(() => {
    setItems([{ planId: null, plan: null, quantity: '' }]);
    setOrdersCurrentPage(1);
  }, [props.activeSpecialty]);

  useEffect(() => {
    dispatch(fetchGroupOrders());
  }, []);

  // Handle create success
  useEffect(() => {
    if (props.createGroupOrder.success) {
      const order = props.createGroupOrder.data;

      if (order.paymentType === 'individual') {
        // Individual payment — codes generated immediately, show codes
        const codesList = (order.codes || []).map((c) => c.code).join('\n');
        Swal.fire({
          title: 'Codes Generated!',
          html: `
            <p><strong>${order.codes?.length || order.classSize}</strong> individual payment codes created.</p>
            <p class="text-muted" style="font-size:0.85rem">Each student will pay their discounted price when redeeming.</p>
            <p class="mt-2">Codes have been emailed to the coordinator.</p>
          `,
          icon: 'success',
          confirmButtonText: 'Copy Codes & Close',
        }).then(() => {
          if (codesList) navigator.clipboard.writeText(codesList).catch(() => {});
        });
      } else {
        // Coordinator payment — show payment link
        const paymentLink = `${window.location.origin}/group-payment/${order.id}`;
        Swal.fire({
          title: 'Group Order Created!',
          html: `
            <p>Payment link generated:</p>
            <div style="display:flex;align-items:center;gap:8px;margin:0 auto;max-width:420px;">
              <input type="text" value="${paymentLink}" class="swal2-input" style="font-size:0.85rem;flex:1;margin:0" readonly />
              <button id="swal-copy-btn" type="button" style="background:#5e72e4;color:#fff;border:none;border-radius:5px;padding:8px 12px;cursor:pointer;font-size:1rem;" title="Copy link">
                <i class="fas fa-copy"></i>
              </button>
            </div>
            <p class="mt-2 text-muted" style="font-size:0.85rem">Send this link to the program coordinator.</p>
          `,
          icon: 'success',
          confirmButtonText: 'Copy Link & Close',
          didOpen: () => {
            document.getElementById('swal-copy-btn').addEventListener('click', () => {
              navigator.clipboard.writeText(paymentLink).then(() => {
                const btn = document.getElementById('swal-copy-btn');
                btn.innerHTML = '<i class="fas fa-check"></i>';
                btn.style.background = '#2dce89';
                setTimeout(() => { btn.innerHTML = '<i class="fas fa-copy"></i>'; btn.style.background = '#5e72e4'; }, 1500);
              });
            });
          },
        }).then(() => {
          navigator.clipboard.writeText(paymentLink).catch(() => {});
        });
      }

      // Reset form
      setProgramName(null);
      setSelectedRole(null);
      setSelectedYear(null);
      setContactName('');
      setContactEmail('');
      setItems([{ planId: null, plan: null, quantity: '' }]);
      setDiscountPercent(20);
      setPaymentType('coordinator');
      setErrors({});
      dispatch(createGroupOrderReset());
      dispatch(fetchGroupOrders());
      setActiveTab('2');
    }

    if (props.createGroupOrder.error) {
      Swal.fire('Error', props.createGroupOrder.error.error || 'Something went wrong', 'error');
      dispatch(createGroupOrderReset());
    }
  }, [props.createGroupOrder]);

  const validateForm = () => {
    const errs = {};
    if (!programName?.value?.trim()) errs.programName = 'Program name is required';
    if (!contactName.trim()) errs.contactName = 'Contact name is required';
    if (!contactEmail.trim()) {
      errs.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      errs.contactEmail = 'Enter a valid email address';
    }
    // Validate line items
    const itemErrors = [];
    let hasItemError = false;
    items.forEach((item, idx) => {
      const ie = {};
      if (!item.plan) { ie.plan = 'Select a plan'; hasItemError = true; }
      if (!item.quantity || Number(item.quantity) < 1) { ie.quantity = 'Min 1'; hasItemError = true; }
      itemErrors.push(ie);
    });
    if (hasItemError) errs.items = itemErrors;
    if (discountPercent === '' || Number(discountPercent) < 0 || Number(discountPercent) > 100) {
      errs.discountPercent = 'Enter a value between 0 and 100';
    }
    return errs;
  };

  const handleCreateOrder = (e) => {
    e.preventDefault();
    const errs = validateForm();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    dispatch(
      createGroupOrder({
        programName: programName.value,
        role: selectedRole?.value || '',
        contactName,
        contactEmail,
        items: items.map((item) => ({
          planId: item.plan.id,
          quantity: Number(item.quantity),
        })),
        discountPercent: Number(discountPercent),
        year: selectedYear?.value || '',
        paymentType,
        specialty: props.activeSpecialty,
      })
    );
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    dispatch(fetchGroupOrderDetails(order.id));
    setDetailsModalOpen(true);
  };

  const getStatusBadge = (status) => {
    const map = { pending: 'warning', paid: 'success', expired: 'secondary', request: 'info', rejected: 'default', active: 'primary' };
    return <Badge color={map[status] || 'info'}>{status}</Badge>;
  };

  // Open approval modal pre-filled with request data
  const handleOpenApproval = (order) => {
    setApprovalOrder(order);
    setApprovalPaymentType(order.paymentTypePreference === 'individual' ? 'individual' : 'coordinator');
    setApprovalDiscount(order.discountPercent ?? 20);
    setApprovalYear(order.year ? yearOptions.find((y) => y.value === order.year) || null : null);
    // Map order items to editable state with plan objects
    const mapped = (order.items || []).map((it) => ({
      planId: it.planId,
      // getPlanById searches every specialty — the base list is PM&R only, so a
      // painPrep/sportsPrep order resolved to null and showed no plan.
      plan: getPlanById(it.planId) || null,
      quantity: String(it.quantity),
      quotedBase: it.basePrice != null ? Number(it.basePrice) : null,
    }));
    setApprovalItems(mapped.length > 0 ? mapped : [{ planId: null, plan: null, quantity: '' }]);
    setApprovalModalOpen(true);
  };

  const handleApproveSubmit = () => {
    dispatch(
      approveGroupOrderRequest(approvalOrder.id, {
        paymentType: approvalPaymentType,
        discountPercent: Number(approvalDiscount),
        year: approvalYear?.value || '',
        items: approvalItems
          .filter((it) => it.plan && Number(it.quantity) > 0)
          .map((it) => ({ planId: it.plan.id, quantity: Number(it.quantity) })),
      })
    )
      .then((data) => {
        setApprovalModalOpen(false);
        dispatch(approveGroupOrderRequestReset());
        dispatch(fetchGroupOrders());
        if (data.codes) {
          const codesList = data.codes.map((c) => c.code).join('\n');
          Swal.fire({
            title: 'Request Approved!',
            html: `<p><strong>${data.codes.length}</strong> codes generated and emailed.</p>`,
            icon: 'success',
            confirmButtonText: 'Copy Codes & Close',
          }).then(() => {
            if (codesList) navigator.clipboard.writeText(codesList).catch(() => {});
          });
        } else {
          Swal.fire('Request Approved!', 'Payment link has been emailed to the coordinator.', 'success');
        }
      })
      .catch(() => {
        Swal.fire('Error', props.approveGroupOrderRequest?.error?.error || 'Failed to approve request', 'error');
        dispatch(approveGroupOrderRequestReset());
      });
  };

  const handleReject = (order) => {
    Swal.fire({
      title: 'Reject Request?',
      text: `Reject the group pricing request from ${order.programName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f5365c',
      confirmButtonText: 'Reject',
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(rejectGroupOrderRequest(order.id))
          .then(() => {
            dispatch(rejectGroupOrderRequestReset());
            dispatch(fetchGroupOrders());
            Swal.fire('Rejected', 'The request has been rejected.', 'success');
          })
          .catch(() => {
            Swal.fire('Error', 'Failed to reject request', 'error');
            dispatch(rejectGroupOrderRequestReset());
          });
      }
    });
  };

  // Approval modal item helpers
  const approvalAddItem = () => setApprovalItems((prev) => [...prev, { planId: null, plan: null, quantity: '' }]);
  const approvalRemoveItem = (idx) => setApprovalItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  const approvalUpdateItem = (idx, field, value) => {
    setApprovalItems((prev) => prev.map((item, i) => {
      if (i !== idx) return item;
      const next = { ...item, [field]: value };
      // Switching the plan invalidates the quoted price for that line — the
      // program was never quoted for this plan, so use the current rate.
      if (field === 'plan' && value?.id !== item.plan?.id) next.quotedBase = null;
      return next;
    }));
  };

  const clearError = (field) => setErrors((prev) => ({ ...prev, [field]: '' }));

  // Item management
  const addItem = () => setItems((prev) => [...prev, { planId: null, plan: null, quantity: '' }]);
  const removeItem = (idx) => {
    setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    // Clear item errors
    setErrors((prev) => ({ ...prev, items: undefined }));
  };
  const updateItem = (idx, field, value) => {
    setItems((prev) => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
    // Clear item error for this index
    if (errors.items) {
      setErrors((prev) => {
        const newItemErrors = [...(prev.items || [])];
        if (newItemErrors[idx]) newItemErrors[idx] = { ...newItemErrors[idx], [field === 'plan' ? 'plan' : 'quantity']: undefined };
        const hasAny = newItemErrors.some((ie) => ie && (ie.plan || ie.quantity));
        return { ...prev, items: hasAny ? newItemErrors : undefined };
      });
    }
  };

  // Calculate totals across all items
  const itemSummaries = items.map((item) => {
    if (!item.plan) return null;
    const base = item.plan.price;
    const perSeat = +(base * (1 - discountPercent / 100)).toFixed(2);
    const qty = Number(item.quantity) || 0;
    return { plan: item.plan, basePrice: base, pricePerSeat: perSeat, quantity: qty, subtotal: +(perSeat * qty).toFixed(2) };
  });
  const totalSeats = itemSummaries.reduce((sum, s) => sum + (s?.quantity || 0), 0);
  const totalAmount = +itemSummaries.reduce((sum, s) => sum + (s?.subtotal || 0), 0).toFixed(2);

  // Approval modal plan options & summary — scoped to the order's specialty so an
  // admin cannot re-plan a Pain order onto PM&R plans.
  const approvalPlanOptions = getPlansForSpecialty(approvalOrder?.specialty || 'pmr').map((p) => ({
    value: p.id,
    label: `${p.name} — $${p.price}`,
    plan: p,
  }));
  const approvalSummaries = approvalItems.map((item) => {
    if (!item.plan) return null;
    // Mirror the backend: an already-quoted line keeps the price the program was quoted.
    const base = item.quotedBase != null ? item.quotedBase : item.plan.price;
    const perSeat = +(base * (1 - approvalDiscount / 100)).toFixed(2);
    const qty = Number(item.quantity) || 0;
    return {
      plan: item.plan,
      basePrice: base,
      isHonoringQuote: item.quotedBase != null && item.quotedBase !== item.plan.price,
      pricePerSeat: perSeat,
      quantity: qty,
      subtotal: +(perSeat * qty).toFixed(2),
    };
  });
  const approvalTotalSeats = approvalSummaries.reduce((sum, s) => sum + (s?.quantity || 0), 0);
  const approvalTotalAmount = +approvalSummaries.reduce((sum, s) => sum + (s?.subtotal || 0), 0).toFixed(2);

  // Filter orders by active specialty (legacy orders without specialty default to 'pmr')
  const filteredOrders = (props.groupOrders.data || []).filter(
    (order) => (order.specialty || 'pmr') === props.activeSpecialty
  );

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">Group Orders</h3>
              </CardHeader>
              <CardBody>
                <Nav tabs className="mb-4">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '1' })}
                      onClick={() => setActiveTab('1')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fas fa-plus-circle mr-2" />
                      Create Order
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === '2' })}
                      onClick={() => setActiveTab('2')}
                      style={{ cursor: 'pointer' }}
                    >
                      <i className="fas fa-list mr-2" />
                      All Orders ({filteredOrders.length})
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab}>
                  {/* Create Order Tab */}
                  <TabPane tabId="1">
                    <Form onSubmit={handleCreateOrder}>
                      <Row>
                        <Col lg="8">
                          {/* Program Info Section */}
                          <Card className="border mb-4">
                            <CardHeader className="bg-white py-3">
                              <h5 className="mb-0">
                                <i className="fas fa-building mr-2 text-primary" />
                                Program Information
                              </h5>
                            </CardHeader>
                            <CardBody>
                              <FormGroup>
                                <Label className="font-weight-bold">Program Name <span className="text-danger">*</span></Label>
                                <CreatableSelect
                                  options={allPmrProgramOptions}
                                  value={programName}
                                  onChange={(val) => { setProgramName(val); clearError('programName'); }}
                                  styles={{
                                    ...customDropdownWithBorder,
                                    control: (base, state) => ({
                                      ...customDropdownWithBorder.control(base, state),
                                      ...(errors.programName ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                                    }),
                                  }}
                                  placeholder="Search or type a program name..."
                                  isClearable
                                  formatCreateLabel={(inputValue) => `Use "${inputValue}"`}
                                />
                                {errors.programName && <small className="text-danger">{errors.programName}</small>}
                              </FormGroup>
                              <Row>
                                <Col md="6">
                                  <FormGroup>
                                    <Label className="font-weight-bold">Role</Label>
                                    <Select
                                      options={roleOptions}
                                      value={selectedRole}
                                      onChange={(val) => setSelectedRole(val)}
                                      styles={customDropdownWithBorder}
                                      placeholder="Select role (optional)"
                                      isClearable
                                    />
                                  </FormGroup>
                                </Col>
                                <Col md="6">
                                  <FormGroup>
                                    <Label className="font-weight-bold">Year</Label>
                                    <Select
                                      options={yearOptions}
                                      value={selectedYear}
                                      onChange={(val) => setSelectedYear(val)}
                                      styles={customDropdownWithBorder}
                                      placeholder="Select PGY year (optional)"
                                      isClearable
                                    />
                                  </FormGroup>
                                </Col>
                              </Row>
                              <Row>
                                <Col md="6">
                                  <FormGroup>
                                    <Label className="font-weight-bold">Contact Name <span className="text-danger">*</span></Label>
                                    <Input
                                      value={contactName}
                                      invalid={!!errors.contactName}
                                      onChange={(e) => { setContactName(e.target.value); clearError('contactName'); }}
                                      placeholder="Program Director or Coordinator"
                                    />
                                    {errors.contactName && <FormFeedback>{errors.contactName}</FormFeedback>}
                                  </FormGroup>
                                </Col>
                                <Col md="6">
                                  <FormGroup>
                                    <Label className="font-weight-bold">Contact Email <span className="text-danger">*</span></Label>
                                    <Input
                                      type="email"
                                      value={contactEmail}
                                      invalid={!!errors.contactEmail}
                                      onChange={(e) => { setContactEmail(e.target.value); clearError('contactEmail'); }}
                                      placeholder="coordinator@hospital.edu"
                                    />
                                    {errors.contactEmail && <FormFeedback>{errors.contactEmail}</FormFeedback>}
                                  </FormGroup>
                                </Col>
                              </Row>
                              <FormGroup className="mt-3">
                                <Label className="font-weight-bold">Payment Type</Label>
                                <div>
                                  <Button
                                    color={paymentType === 'coordinator' ? 'primary' : 'secondary'}
                                    outline={paymentType !== 'coordinator'}
                                    size="sm"
                                    className="mr-2"
                                    onClick={() => setPaymentType('coordinator')}
                                    type="button"
                                  >
                                    <i className="fas fa-user-tie mr-1" />
                                    Coordinator Pays
                                  </Button>
                                  <Button
                                    color={paymentType === 'individual' ? 'primary' : 'secondary'}
                                    outline={paymentType !== 'individual'}
                                    size="sm"
                                    onClick={() => setPaymentType('individual')}
                                    type="button"
                                  >
                                    <i className="fas fa-users mr-1" />
                                    Individual Payment
                                  </Button>
                                </div>
                                <small className="text-muted mt-1 d-block">
                                  {paymentType === 'coordinator'
                                    ? 'Coordinator pays the total amount. A payment link will be sent.'
                                    : 'Each student pays individually at the discounted price. Codes are generated immediately.'}
                                </small>
                              </FormGroup>
                            </CardBody>
                          </Card>

                          {/* Plan & Pricing Section */}
                          <Card className="border mb-4">
                            <CardHeader className="bg-white py-3">
                              <h5 className="mb-0">
                                <i className="fas fa-tags mr-2 text-success" />
                                Plans & Pricing
                              </h5>
                            </CardHeader>
                            <CardBody>
                              <Row className="mb-3">
                                <Col md="4">
                                  <FormGroup className="mb-0">
                                    <Label className="font-weight-bold">Group Discount %</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={discountPercent}
                                      invalid={!!errors.discountPercent}
                                      onChange={(e) => { setDiscountPercent(e.target.value); clearError('discountPercent'); }}
                                    />
                                    {errors.discountPercent && <FormFeedback>{errors.discountPercent}</FormFeedback>}
                                  </FormGroup>
                                </Col>
                              </Row>
                              <hr />
                              <Label className="font-weight-bold">Line Items <span className="text-danger">*</span></Label>
                              {items.map((item, idx) => {
                                const itemErr = errors.items?.[idx];
                                return (
                                  <Row key={idx} className="mb-2 align-items-start">
                                    <Col md="7">
                                      <Select
                                        options={planOptions}
                                        value={item.plan ? planOptions.find((o) => o.value === item.plan.id) : null}
                                        onChange={(val) => updateItem(idx, 'plan', val?.plan || null)}
                                        styles={{
                                          ...customDropdownWithBorder,
                                          control: (base, state) => ({
                                            ...customDropdownWithBorder.control(base, state),
                                            ...(itemErr?.plan ? { borderColor: '#fb6340', '&:hover': { borderColor: '#fb6340' } } : {}),
                                          }),
                                        }}
                                        placeholder="Select plan..."
                                        isClearable
                                      />
                                      {itemErr?.plan && <small className="text-danger">{itemErr.plan}</small>}
                                    </Col>
                                    <Col md="3">
                                      <Input
                                        type="number"
                                        min="1"
                                        value={item.quantity}
                                        invalid={!!itemErr?.quantity}
                                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                        placeholder="Qty"
                                      />
                                      {itemErr?.quantity && <FormFeedback>{itemErr.quantity}</FormFeedback>}
                                    </Col>
                                    <Col md="2">
                                      <Button
                                        color="danger"
                                        outline
                                        size="sm"
                                        className="mt-1"
                                        disabled={items.length <= 1}
                                        onClick={() => removeItem(idx)}
                                      >
                                        <i className="fas fa-trash" />
                                      </Button>
                                    </Col>
                                  </Row>
                                );
                              })}
                              <Button color="default" outline size="sm" className="mt-2" onClick={addItem}>
                                <i className="fas fa-plus mr-1" /> Add Plan
                              </Button>
                            </CardBody>
                          </Card>
                        </Col>

                        {/* Price Summary Sidebar */}
                        <Col lg="4">
                          <Card className="border shadow-sm" style={{ position: 'sticky', top: '1rem' }}>
                            <CardHeader className="bg-gradient-primary py-3">
                              <h5 className="mb-0 text-white">
                                <i className="fas fa-calculator mr-2" />
                                Order Summary
                              </h5>
                            </CardHeader>
                            <CardBody>
                              {itemSummaries.some((s) => s && s.quantity > 0) ? (
                                <>
                                  {itemSummaries.map((s, idx) => s && s.quantity > 0 && (
                                    <div key={idx} className="mb-2 pb-2" style={idx < itemSummaries.filter(Boolean).length - 1 ? { borderBottom: '1px dashed #e9ecef' } : {}}>
                                      <div className="d-flex justify-content-between">
                                        <span className="font-weight-bold" style={{ fontSize: '0.85rem' }}>{s.plan.name}</span>
                                        <span style={{ fontSize: '0.85rem' }}>{s.quantity} seat{s.quantity !== 1 ? 's' : ''}</span>
                                      </div>
                                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.8rem' }}>
                                        <span>${s.pricePerSeat}/seat</span>
                                        <span>${s.subtotal}</span>
                                      </div>
                                    </div>
                                  ))}
                                  {Number(discountPercent) > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Group Discount</span>
                                      <span className="text-success">-{discountPercent}%</span>
                                    </div>
                                  )}
                                  <div className="d-flex justify-content-between mb-2">
                                    <span className="text-muted">Total Seats</span>
                                    <span>{totalSeats}</span>
                                  </div>
                                  <hr />
                                  <div className="d-flex justify-content-between align-items-center">
                                    <span className="h4 mb-0">Total</span>
                                    <span className="h3 mb-0 text-success">${totalAmount}</span>
                                  </div>
                                </>
                              ) : (
                                <div className="text-center text-muted py-3">
                                  <i className="fas fa-info-circle mb-2" style={{ fontSize: '1.5rem' }} />
                                  <p className="mb-0" style={{ fontSize: '0.85rem' }}>
                                    Add plans and quantities to see pricing
                                  </p>
                                </div>
                              )}

                              {paymentType === 'individual' && itemSummaries.some((s) => s && s.quantity > 0) && (
                                <div className="mt-2 mb-0">
                                  <small className="text-muted">Per Student Prices:</small>
                                  {itemSummaries.map((s, idx) => s && s.quantity > 0 && (
                                    <div key={idx} className="d-flex justify-content-between" style={{ fontSize: '0.8rem' }}>
                                      <span>{s.plan.name}</span>
                                      <span className="font-weight-bold">${s.pricePerSeat}/student</span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <Button
                                color="primary"
                                type="submit"
                                className="w-100 mt-4"
                                size="lg"
                                disabled={props.createGroupOrder.loading}
                              >
                                {props.createGroupOrder.loading ? (
                                  <Spinner size="sm" />
                                ) : paymentType === 'individual' ? (
                                  <>
                                    <i className="fas fa-ticket-alt mr-2" />
                                    Generate Codes
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-link mr-2" />
                                    Generate Payment Link
                                  </>
                                )}
                              </Button>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    </Form>
                  </TabPane>

                  {/* All Orders Tab */}
                  <TabPane tabId="2">
                    {props.groupOrders.loading ? (
                      <div className="text-center py-4">
                        <Spinner />
                      </div>
                    ) : filteredOrders.length === 0 ? (
                      <p className="text-muted text-center py-4">No group orders for this specialty.</p>
                    ) : (
                      <>
                      <div className="table-responsive">
                        <Table className="align-items-center" hover>
                          <thead className="thead-light">
                            <tr>
                              <th>Action</th>
                              <th>Status</th>
                              <th>Source</th>
                              <th>Type</th>
                              <th>Program</th>
                              <th>Year</th>
                              <th>Contact</th>
                              <th>Plan</th>
                              <th>Seats</th>
                              <th>Total</th>
                              <th>Progress</th>
                              <th>Created</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...filteredOrders]
                              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                              .slice((ordersCurrentPage - 1) * ordersPerPage, ordersCurrentPage * ordersPerPage)
                              .map((order) => (
                              <tr key={order.id}>
                                <td>
                                  {order.status === 'request' ? (
                                    <>
                                      <Button size="sm" color="info" outline className="mr-1" onClick={() => handleViewDetails(order)}>
                                        Details
                                      </Button>
                                      <Button size="sm" color="success" className="mr-1" onClick={() => handleOpenApproval(order)}>
                                        Approve
                                      </Button>
                                      <Button size="sm" color="danger" outline onClick={() => handleReject(order)}>
                                        Reject
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        color="info"
                                        outline
                                        onClick={() => handleViewDetails(order)}
                                      >
                                        Details
                                      </Button>
                                      {order.status === 'pending' && order.paymentType !== 'individual' && (
                                        <Button
                                          size="sm"
                                          color="primary"
                                          outline
                                          className="ml-1"
                                          onClick={() => {
                                            const link = `${window.location.origin}/group-payment/${order.id}`;
                                            navigator.clipboard.writeText(link).then(() => {
                                              Swal.fire({
                                                title: 'Copied!',
                                                text: 'Payment link copied to clipboard.',
                                                icon: 'success',
                                                timer: 1500,
                                                showConfirmButton: false,
                                              });
                                            });
                                          }}
                                        >
                                          Copy Link
                                        </Button>
                                      )}
                                    </>
                                  )}
                                </td>
                                <td>{getStatusBadge(order.status)}</td>
                                <td>
                                  <small className={`text-uppercase font-weight-bold ${order.source === 'public' ? 'text-info' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                                    {order.source === 'public' ? 'Request' : 'Manual'}
                                  </small>
                                </td>
                                <td>
                                  {order.paymentType ? (
                                    <small className={`text-uppercase font-weight-bold ${order.paymentType === 'individual' ? 'text-info' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                                      {order.paymentType === 'individual' ? 'Individual' : 'Coordinator'}
                                    </small>
                                  ) : order.paymentTypePreference ? (
                                    <small className={`text-uppercase font-weight-bold ${order.paymentTypePreference === 'individual' ? 'text-info' : 'text-muted'}`} style={{ fontSize: '0.7rem' }}>
                                      {order.paymentTypePreference === 'individual' ? 'Individual' : 'Coordinator'}
                                    </small>
                                  ) : (
                                    <small className="text-muted" style={{ fontSize: '0.7rem' }}>—</small>
                                  )}
                                </td>
                                <td className="font-weight-bold">{order.programName}</td>
                                <td><small>{order.year || '—'}</small></td>
                                <td>
                                  <small>{order.contactName}</small>
                                  <br />
                                  <small className="text-muted">{order.contactEmail}</small>
                                </td>
                                <td><small>{order.items
                                  ? order.items.map((it) => `${it.quantity}x ${getPlanById(it.planId)?.months || '?'}mo`).join(', ')
                                  : getPlanById(order.planId)?.name || order.planId}</small></td>
                                <td>{order.classSize}</td>
                                <td>${order.totalAmount}</td>
                                <td>
                                  {order.status === 'request' || order.status === 'rejected' ? (
                                    <small className="text-muted">—</small>
                                  ) : order.paymentType === 'individual' ? (
                                    <small>{order.codesPaid || 0}/{order.classSize} paid</small>
                                  ) : (
                                    <small>{order.codesClaimed}/{order.codesGenerated} claimed</small>
                                  )}
                                </td>
                                <td>
                                  <small>
                                    {new Date(order.createdAt).toLocaleDateString()}
                                  </small>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                      {filteredOrders.length > ordersPerPage && (
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center mt-3">
                          <div className="text-muted small mb-2 mb-sm-0">
                            Showing {((ordersCurrentPage - 1) * ordersPerPage) + 1} to {Math.min(ordersCurrentPage * ordersPerPage, filteredOrders.length)} of {filteredOrders.length} orders
                          </div>
                          <div className="d-flex align-items-center">
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => setOrdersCurrentPage(prev => Math.max(1, prev - 1))}
                              disabled={ordersCurrentPage === 1}
                              className="mr-2"
                            >
                              <i className="fas fa-chevron-left"></i>
                              <span className="d-none d-sm-inline ml-1">Previous</span>
                            </Button>
                            <span className="mx-2 text-nowrap">
                              Page {ordersCurrentPage} of {Math.ceil(filteredOrders.length / ordersPerPage) || 1}
                            </span>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => setOrdersCurrentPage(prev => Math.min(Math.ceil(filteredOrders.length / ordersPerPage), prev + 1))}
                              disabled={ordersCurrentPage >= Math.ceil(filteredOrders.length / ordersPerPage)}
                              className="ml-2"
                            >
                              <span className="d-none d-sm-inline mr-1">Next</span>
                              <i className="fas fa-chevron-right"></i>
                            </Button>
                          </div>
                        </div>
                      )}
                      </>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {detailsModalOpen && (
          <GroupOrderDetailsModal
            isOpen={detailsModalOpen}
            toggle={() => setDetailsModalOpen(false)}
            orderDetails={props.groupOrderDetails.data}
            loading={props.groupOrderDetails.loading}
            activatingPaidCodes={props.activatePaidCodes?.loading}
          />
        )}

        {/* Approval Modal */}
        <Modal isOpen={approvalModalOpen} toggle={() => setApprovalModalOpen(false)} size="lg">
          <ModalHeader toggle={() => setApprovalModalOpen(false)}>
            Approve Group Pricing Request
          </ModalHeader>
          <ModalBody>
            {approvalOrder && (
              <Row>
                <Col md="7">
                  {/* Request Info (read-only) */}
                  <Card className="border mb-3">
                    <CardHeader className="bg-white py-2">
                      <h6 className="mb-0"><i className="fas fa-info-circle mr-2 text-info" />Request Details</h6>
                    </CardHeader>
                    <CardBody className="py-3">
                      <div className="mb-1"><strong>Program:</strong> {approvalOrder.programName}</div>
                      <div className="mb-1"><strong>Contact:</strong> {approvalOrder.contactName} ({approvalOrder.contactEmail})</div>
                      {approvalOrder.specialty && approvalOrder.specialty !== 'pmr' && (
                        <div className="mb-1"><strong>Specialty:</strong> {SPECIALTIES[approvalOrder.specialty]?.fullName || approvalOrder.specialty}</div>
                      )}
                      {approvalOrder.role && <div className="mb-1"><strong>Role:</strong> {approvalOrder.role}</div>}
                      {approvalOrder.paymentTypePreference && (
                        <div className="mb-1"><strong>Payment Preference:</strong> {approvalOrder.paymentTypePreference === 'individual' ? 'Individual Payment' : 'Coordinator Pays'}</div>
                      )}
                      {approvalOrder.notes && <div className="mb-1"><strong>Notes:</strong> {approvalOrder.notes}</div>}
                      {approvalOrder.isEntireClass && (
                        <div className="mb-1">
                          <Badge color="info">{approvalOrder.isEntireProgram ? 'Entire Program' : 'Entire Class'}</Badge>
                        </div>
                      )}
                    </CardBody>
                  </Card>

                  {/* Editable settings */}
                  <FormGroup>
                    <Label className="font-weight-bold">Payment Type <span className="text-danger">*</span></Label>
                    <div>
                      <Button
                        color={approvalPaymentType === 'coordinator' ? 'primary' : 'secondary'}
                        outline={approvalPaymentType !== 'coordinator'}
                        size="sm"
                        className="mr-2"
                        onClick={() => setApprovalPaymentType('coordinator')}
                        type="button"
                      >
                        <i className="fas fa-user-tie mr-1" />Coordinator Pays
                      </Button>
                      <Button
                        color={approvalPaymentType === 'individual' ? 'primary' : 'secondary'}
                        outline={approvalPaymentType !== 'individual'}
                        size="sm"
                        onClick={() => setApprovalPaymentType('individual')}
                        type="button"
                      >
                        <i className="fas fa-users mr-1" />Individual Payment
                      </Button>
                    </div>
                  </FormGroup>

                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="font-weight-bold">Discount %</Label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          value={approvalDiscount}
                          onChange={(e) => setApprovalDiscount(e.target.value)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label className="font-weight-bold">Year</Label>
                        <Select
                          options={yearOptions}
                          value={approvalYear}
                          onChange={(val) => setApprovalYear(val)}
                          placeholder="PGY..."
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ ...customDropdownWithBorder, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Label className="font-weight-bold">Line Items</Label>
                  {approvalItems.map((item, idx) => (
                    <Row key={idx} className="mb-2 align-items-start">
                      <Col md="7">
                        <Select
                          options={approvalPlanOptions}
                          value={item.plan ? approvalPlanOptions.find((o) => o.value === item.plan.id) : null}
                          onChange={(val) => approvalUpdateItem(idx, 'plan', val?.plan || null)}
                          placeholder="Select plan..."
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ ...customDropdownWithBorder, menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </Col>
                      <Col md="3">
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => approvalUpdateItem(idx, 'quantity', e.target.value)}
                          placeholder="Qty"
                        />
                      </Col>
                      <Col md="2">
                        <Button color="danger" outline size="sm" className="mt-1" disabled={approvalItems.length <= 1} onClick={() => approvalRemoveItem(idx)}>
                          <i className="fas fa-trash" />
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button color="default" outline size="sm" className="mt-1" onClick={approvalAddItem}>
                    <i className="fas fa-plus mr-1" /> Add Plan
                  </Button>
                </Col>

                {/* Summary sidebar */}
                <Col md="5">
                  <Card className="border shadow-sm">
                    <CardHeader className="bg-gradient-primary py-2">
                      <h6 className="mb-0 text-white"><i className="fas fa-calculator mr-2" />Order Summary</h6>
                    </CardHeader>
                    <CardBody>
                      {approvalSummaries.some((s) => s && s.quantity > 0) ? (
                        <>
                          {approvalSummaries.map((s, idx) => s && s.quantity > 0 && (
                            <div key={idx} className="mb-2 pb-2" style={idx < approvalSummaries.filter(Boolean).length - 1 ? { borderBottom: '1px dashed #e9ecef' } : {}}>
                              <div className="d-flex justify-content-between" style={{ fontSize: '0.85rem' }}>
                                <span className="font-weight-bold">{s.plan.name}</span>
                                <span>{s.quantity} seat{s.quantity !== 1 ? 's' : ''}</span>
                              </div>
                              <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.8rem' }}>
                                <span>${s.pricePerSeat}/seat</span>
                                <span>${s.subtotal}</span>
                              </div>
                              {s.isHonoringQuote && (
                                <div style={{ fontSize: '0.75rem' }} className="text-info">
                                  <i className="fas fa-lock mr-1" />
                                  Honoring quoted base ${s.basePrice}/seat (current list ${s.plan.price})
                                </div>
                              )}
                            </div>
                          ))}
                          {Number(approvalDiscount) > 0 && (
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">Discount</span>
                              <span className="text-success">-{approvalDiscount}%</span>
                            </div>
                          )}
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">Total Seats</span>
                            <span>{approvalTotalSeats}</span>
                          </div>
                          <hr className="my-2" />
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="h5 mb-0">Total</span>
                            <span className="h4 mb-0 text-success">${approvalTotalAmount}</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-center text-muted py-3" style={{ fontSize: '0.85rem' }}>
                          Configure items to see pricing
                        </div>
                      )}

                      <Button
                        color="success"
                        className="w-100 mt-3"
                        disabled={props.approveGroupOrderRequest?.loading || approvalTotalSeats === 0}
                        onClick={handleApproveSubmit}
                      >
                        {props.approveGroupOrderRequest?.loading ? (
                          <Spinner size="sm" />
                        ) : (
                          <>
                            <i className="fas fa-check mr-2" />
                            Approve & Create Order
                          </>
                        )}
                      </Button>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  createGroupOrder: state.createGroupOrder,
  groupOrders: state.fetchGroupOrders,
  groupOrderDetails: state.fetchGroupOrderDetails,
  activatePaidCodes: state.activatePaidCodes,
  approveGroupOrderRequest: state.approveGroupOrderRequest,
  rejectGroupOrderRequest: state.rejectGroupOrderRequest,
  activeSpecialty: state.specialty?.activeSpecialty || 'pmr',
});

export default connect(mapStateToProps)(ManageGroupOrdersPage);
